import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Download, Mail, Trash2, Users } from "lucide-react";

import { apiFetch } from "@/lib/apiClient";

/**
 * TCGMA-175 — the operator half of trade-show lead capture: read the signups,
 * and (the actual point) get the emails OUT so they can be mailed.
 *
 * The event filter is why the leads are tagged at all — it's what tells you
 * which shows were worth the table fee.
 *
 * Export defaults to CONSENTED rows only. Someone who entered the prize draw but
 * didn't tick "email me" is a raffle entrant, not a mailing-list subscriber, and
 * mailing them anyway is how you end up in a spam folder (and on the wrong side
 * of Brevo's opt-in expectations). "Include non-consented" is there for when you
 * need the full list to draw the winner.
 */

export const Route = createFileRoute("/tools/leads")({
  component: LeadsPage,
});

interface Lead {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  interests: string[];
  marketingConsent: boolean;
  raffleEntry: boolean;
  eventName: string | null;
  source: string;
  notes: string | null;
  createdAt: string;
}

interface EventSummary {
  eventName: string | null;
  count: number;
  consented: number;
}

function LeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [event, setEvent] = useState<string>("");
  const [includeAll, setIncludeAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (ev: string) => {
    setLoading(true);
    setError(null);
    try {
      const qs = ev ? `?event=${encodeURIComponent(ev)}` : "";
      const [leadRes, evRes] = await Promise.all([
        apiFetch(`/api/leads${qs}`),
        apiFetch("/api/leads/events"),
      ]);
      const leadJson = (await leadRes.json()) as { leads: Lead[] };
      const evJson = (await evRes.json()) as { events: EventSummary[] };
      setLeads(leadJson.leads ?? []);
      setEvents(evJson.events ?? []);
    } catch {
      setError("Couldn't load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(event);
  }, [event]);

  const consented = useMemo(
    () => leads.filter((l) => l.marketingConsent).length,
    [leads],
  );

  const exportUrl = `/api/leads/export.csv?${new URLSearchParams({
    ...(event ? { event } : {}),
    ...(includeAll ? { all: "true" } : {}),
  })}`;

  const remove = async (id: string) => {
    if (!confirm("Delete this lead?")) return;
    await apiFetch(`/api/leads/${id}`, { method: "DELETE" });
    setLeads((cur) => cur.filter((l) => l.id !== id));
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Users size={22} /> Leads
          </h1>
          <p className="text-sm text-muted-foreground">
            Signups captured at shows — export the consented ones and mail them.
          </p>
        </div>
        <a
          href={exportUrl}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Download size={16} /> Export CSV
        </a>
      </div>

      {/* Per-event counts — the ROI signal. */}
      {events.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setEvent("")}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              event === "" ? "border-primary bg-primary/10" : "border-border"
            }`}
          >
            All ({events.reduce((n, e) => n + e.count, 0)})
          </button>
          {events.map((e) => (
            <button
              key={e.eventName ?? "__none"}
              onClick={() => setEvent(e.eventName ?? "")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                event === (e.eventName ?? "")
                  ? "border-primary bg-primary/10"
                  : "border-border"
              }`}
            >
              {e.eventName ?? "No event"} ({e.count} · {e.consented} opted in)
            </button>
          ))}
        </div>
      )}

      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={includeAll}
          onChange={(e) => setIncludeAll(e.target.checked)}
        />
        Include non-consented in the export (for drawing the raffle winner — do
        not mail these)
      </label>

      {loading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">{error}</p>}

      {!loading && leads.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <Mail className="mx-auto mb-3 text-muted-foreground" size={28} />
          <p className="font-medium">No leads yet</p>
          <p className="text-sm text-muted-foreground">
            They'll appear here as people sign up at the booth.
          </p>
        </div>
      )}

      {leads.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground">
            {leads.length} lead{leads.length === 1 ? "" : "s"} · {consented}{" "}
            opted in to email
          </p>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Collects</th>
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">Consent</th>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-4 py-3 font-medium">{l.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {l.name ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {l.interests.length ? l.interests.join(", ") : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {l.eventName ?? "—"}
                      <span className="ml-1 text-xs opacity-60">
                        ({l.source})
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {l.marketingConsent ? (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          opted in
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          draw only
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(l.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(l.id)}
                        className="text-muted-foreground hover:text-destructive"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
